// bagitem.component.ts
import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastComponent } from '../../layout/toast/toast.component';
import Swal from 'sweetalert2';

// 背包物品主檔
interface BagItem {
    id?: string;        // 新增時可能沒有 id
    type: string;       // 1：贈送用 2：自己使用 3：其他
    gender: string;     // 0：不限 1：男 2：女
    name: string;
    des_action: string;
    emoji: string;
    pic: string;
    points: string;
    status: string;     // 1：上架 0：停用 -1：刪除
    start_dt: string;
    end_dt: string;
    create_dt?: string;
    update_dt?: string;
}

interface ApiResponse {
    status: boolean;
    msg: string;
    data: BagItem[];
    total_count: number;
}

@Component({
    selector: 'app-bagitem',
    templateUrl: './bagitem.component.html',
    styleUrls: ['./bagitem.component.css']
})
export class BagitemComponent implements OnInit {
    @ViewChild(ToastComponent, { static: true }) toastComponent: ToastComponent;

    loading: boolean = true;
    items: BagItem[] = [];
    searchTerm: string = '';

    // 分頁
    pageobj = {
        page: 0,
        count: 10,
        total_count: 0,
    };

    // 彈窗相關
    showItemModal: boolean = false;  // 統一的彈窗狀態
    isEditMode: boolean = false;     // 區分編輯或新增
    editingItem: BagItem | null = null;
    editForm: any = this.emptyForm();

    // 圖片上傳相關
    selectedFile: File | null = null;
    imagePreviewUrl: string | null = null;
    isUploading: boolean = false;

    // 選項
    typeOptions = [
        { value: '1', label: '贈送用' },
        { value: '2', label: '自己使用' },
        { value: '3', label: '其他' },
    ];

    genderOptions = [
        { value: '0', label: '不限' },
        { value: '1', label: '男' },
        { value: '2', label: '女' },
    ];

    constructor(
        public httpClient: HttpClient,
    ) {}

    ngOnInit(): void {
        this.init_list();
    }

    emptyForm() {
        return {
            id: '',
            type: '1',
            gender: '1',
            name: '',
            des_action: '',
            emoji: '',
            points: '0',
            status: '1',
            start_dt: '',
            end_dt: '',
            pic: '',
            tmp_img_b64: '',
        };
    }

    init_list() {
        this.loading = true;
        const body = {
            page: this.pageobj.page,
            count: this.pageobj.count,
            search: this.searchTerm,
        };
        this.httpClient.post<ApiResponse>("/customer_service/get_bag_item_list.php", body).subscribe(res => {
            this.loading = false;
            if (res.status) {
                this.items = res.data || [];
                this.pageobj.total_count = res.total_count;
            } else {
                this.toastComponent.showMessage(res.msg || '載入失敗', 'error');
            }
        }, error => {
            this.loading = false;
            console.error('HTTP Error:', error);
            this.toastComponent.showMessage('載入失敗：網路錯誤', 'error');
        });
    }

    refreshList() {
        this.init_list();
    }

    searchList() {
        this.pageobj.page = 0;
        this.init_list();
    }

    handlepage(e) {
        // app-pagnation 回傳整包 data(含新的 page)
        this.pageobj.page = e.page;
        this.init_list();
    }

    // ----- 顯示輔助 -----
    getTypeText(type: string): string {
        const o = this.typeOptions.find(x => x.value === String(type));
        return o ? o.label : type;
    }

    getGenderText(gender: string): string {
        const o = this.genderOptions.find(x => x.value === String(gender));
        return o ? o.label : gender;
    }

    getStatusText(status: string): string {
        if (String(status) === '1') return '上架';
        if (String(status) === '0') return '停用';
        return '刪除';
    }

    getStatusClass(status: string): string {
        return String(status) === '1' ? 'active' : 'inactive';
    }

    private numberFormatter = new Intl.NumberFormat('zh-TW');

    numberToThousand(str: string | number): string {
        if (!str) return '0';
        return this.numberFormatter.format(Number(str));
    }

    formatDate(dateString: string): string {
        if (!dateString) return '';
        return dateString.split(' ')[0] || dateString;
    }

    trackByItemId(index: number, item: BagItem): string {
        return item.id;
    }

    onImageError(event: any) {
        event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPgo=';
    }

    // ----- 新增 / 編輯 -----
    addNewItem() {
        this.isEditMode = false;
        this.editingItem = null;
        this.editForm = this.emptyForm();
        this.selectedFile = null;
        this.imagePreviewUrl = null;
        this.showItemModal = true;
    }

    editItem(item: BagItem) {
        this.isEditMode = true;
        this.editingItem = { ...item };
        this.editForm = {
            id: item.id,
            type: String(item.type),
            gender: String(item.gender),
            name: item.name,
            des_action: item.des_action,
            emoji: item.emoji,
            points: String(item.points),
            status: String(item.status),
            start_dt: item.start_dt,
            end_dt: item.end_dt,
            pic: item.pic,
            tmp_img_b64: '',
        };
        this.imagePreviewUrl = item.pic;
        this.selectedFile = null;
        this.showItemModal = true;
    }

    closeItemModal() {
        this.showItemModal = false;
        this.isEditMode = false;
        this.editingItem = null;
        this.selectedFile = null;
        this.imagePreviewUrl = null;
        this.isUploading = false;
        this.editForm = this.emptyForm();
    }

    onModalBackdropClick(event: Event) {
        if (event.target === event.currentTarget) {
            this.closeItemModal();
        }
    }

    // ----- 圖片上傳 -----
    onFileSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                this.toastComponent.showMessage('請選擇圖片檔案', 'warning');
                event.target.value = '';
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                this.toastComponent.showMessage('圖片檔案不能超過 5MB', 'warning');
                event.target.value = '';
                return;
            }
            this.selectedFile = file;
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.imagePreviewUrl = e.target.result;       // 預覽
                this.editForm.tmp_img_b64 = e.target.result;  // base64 帶給後端
            };
            reader.onerror = () => {
                this.toastComponent.showMessage('圖片讀取失敗，請重新選擇', 'error');
                this.selectedFile = null;
                this.imagePreviewUrl = this.isEditMode && this.editingItem ? this.editingItem.pic : null;
            };
            reader.readAsDataURL(file);
        }
    }

    resetImage() {
        this.selectedFile = null;
        this.editForm.tmp_img_b64 = '';
        if (this.isEditMode && this.editingItem) {
            this.imagePreviewUrl = this.editingItem.pic;
        } else {
            this.imagePreviewUrl = null;
        }
    }

    // ----- 驗證 -----
    validateForm(): boolean {
        if (!this.editForm.name || !this.editForm.name.trim()) {
            this.toastComponent.showMessage('請輸入物品名稱', 'warning');
            return false;
        }
        if (this.editForm.points === '' || parseInt(this.editForm.points, 10) < 0) {
            this.toastComponent.showMessage('物品價值不可小於 0', 'warning');
            return false;
        }
        if (this.editForm.start_dt && this.editForm.end_dt &&
            new Date(this.editForm.start_dt).getTime() > new Date(this.editForm.end_dt).getTime()) {
            this.toastComponent.showMessage('起始時間不可晚於結束時間', 'warning');
            return false;
        }
        if (!this.isEditMode && !this.selectedFile && !this.imagePreviewUrl) {
            this.toastComponent.showMessage('請上傳物品圖片', 'warning');
            return false;
        }
        return true;
    }

    // ----- 儲存(新增/修改) -----
    save() {
        if (!this.validateForm()) {
            return;
        }
        this.isUploading = true;
        const body = { data: this.editForm };
        this.httpClient.post<ApiResponse>("/customer_service/set_bag_item.php", body).subscribe(res => {
            this.isUploading = false;
            if (res.status) {
                this.closeItemModal();
                this.toastComponent.showMessage(res.msg || (this.isEditMode ? '更新成功' : '新增成功'), 'success');
                this.init_list();
            } else {
                this.toastComponent.showMessage(res.msg || '儲存失敗', 'error');
            }
        }, error => {
            this.isUploading = false;
            console.error('儲存錯誤:', error);
            this.toastComponent.showMessage('儲存失敗：網路錯誤', 'error');
        });
    }

    // ----- 上下架切換 -----
    toggleStatus(item: BagItem) {
        const newStatus = String(item.status) === '1' ? '0' : '1';
        const action = newStatus === '1' ? '上架' : '停用';
        Swal.fire({
            title: `確定要${action}「${item.name}」嗎？`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '確定',
            cancelButtonText: '取消',
        }).then((result) => {
            if (result.isConfirmed) {
                const body = { data: { ...item, status: newStatus, tmp_img_b64: '' } };
                this.httpClient.post<ApiResponse>("/customer_service/set_bag_item.php", body).subscribe(res => {
                    if (res.status) {
                        this.toastComponent.showMessage(`已${action}`, 'success');
                        this.init_list();
                    } else {
                        this.toastComponent.showMessage(res.msg || '操作失敗', 'error');
                    }
                }, error => {
                    console.error(error);
                    this.toastComponent.showMessage('操作失敗：網路錯誤', 'error');
                });
            }
        });
    }

    // ----- 軟刪除(status=-1) -----
    deleteItem(item: BagItem) {
        Swal.fire({
            title: `確定要刪除「${item.name}」嗎？`,
            text: '刪除後將不再顯示於列表',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: '刪除',
            cancelButtonText: '取消',
        }).then((result) => {
            if (result.isConfirmed) {
                const body = { data: { ...item, status: '-1', tmp_img_b64: '' } };
                this.httpClient.post<ApiResponse>("/customer_service/set_bag_item.php", body).subscribe(res => {
                    if (res.status) {
                        Swal.fire('已刪除完成!', '', 'success');
                        this.init_list();
                    } else {
                        this.toastComponent.showMessage(res.msg || '刪除失敗', 'error');
                    }
                }, error => {
                    console.error(error);
                    this.toastComponent.showMessage('刪除失敗：網路錯誤', 'error');
                });
            }
        });
    }
}
